import React, { useEffect, useMemo } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';
import Login from '../components/Login';
import Register from '../components/Register';
import '../styles/auth.css';

const AUTH_JOKES = [
    'This recipe is private — like a secret family bobotie recipe. You need to sign in to get the key to the kitchen.',
    'Sorry, this dish is off the menu for guests. Sign in to see what’s cooking in your account.',
    'No soup for strangers! This one’s simmering behind a login wall — sign in and tuck in.',
    'You’ve found a recipe with a “do not share” sticker on it. Sign in as the owner to take a look.',
];

function AuthGate({ redirectTo }) {
    const joke = useMemo(
        () => AUTH_JOKES[Math.floor(Math.random() * AUTH_JOKES.length)],
        []
    );

    const loginPath = redirectTo
        ? `/auth/login?redirect=${encodeURIComponent(redirectTo)}`
        : '/auth/login';
    const registerPath = redirectTo
        ? `/auth/register?redirect=${encodeURIComponent(redirectTo)}`
        : '/auth/register';

    const isRecipeRedirect = redirectTo?.startsWith('/recipe/');

    return (
        <div className="auth auth-gate-page">
            <div className="auth-gate-card">
                <div className="auth-gate-icon" aria-hidden="true">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                    </svg>
                </div>
                <h1 className="auth-gate-title">Kitchen&apos;s closed (for now)</h1>
                <p className="auth-gate-lead">
                    {isRecipeRedirect
                        ? 'You don’t have access to this recipe — it’s private to the person who created it.'
                        : 'You need to sign in to view this page.'}
                </p>
                <p className="auth-gate-joke">{joke}</p>
                <div className="auth-gate-actions">
                    <Link to={loginPath} className="auth-gate-btn auth-gate-btn-primary">
                        Sign in
                    </Link>
                    <Link to={registerPath} className="auth-gate-btn auth-gate-btn-secondary">
                        Create free account
                    </Link>
                    <Link to="/" className="auth-gate-home">
                        ← Return to home
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default function Auth() {
    const cookies = new Cookies();
    const token = cookies.get('access_token');
    const userId = window.localStorage.getItem('userID');
    const location = useLocation();
    const navigate = useNavigate();
    
    // Check for special redirect query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const redirectTo = urlParams.get('redirect');
    
    // Listen for authentication events from child components
    useEffect(() => {
        const handleAuthSuccess = (event) => {
            if (event.detail && event.detail.success) {
                console.log('Auth success event detected');
                
                // Small delay to ensure cookies/localStorage are properly set
                setTimeout(() => {
                    if (redirectTo) {
                        navigate(redirectTo);
                    } else {
                        navigate('/');
                    }
                }, 100);
            }
        };
        
        window.addEventListener('auth-success', handleAuthSuccess);
        
        return () => {
            window.removeEventListener('auth-success', handleAuthSuccess);
        };
    }, [navigate, redirectTo]);

    // If already authenticated, redirect to home or specified redirect path
    if (token && userId) {
        return <Navigate to={redirectTo || '/'} replace />;
    }

    if (location.pathname === '/auth') {
        return <AuthGate redirectTo={redirectTo} />;
    }

    return (
        <div className="auth">
            <div className="auth-container">
                {location.pathname === '/auth/login' ? <Login /> : <Register />}
            </div>
        </div>
    );
}
